/** 小工具：安全轉整數 */
const toInt = (x) => {
  const n = parseInt(x, 10);
  return Number.isFinite(n) ? n : 0;
};

/** 小工具：計算 NP 比例（%） */
const calcNpRatio = (newPatients, appointments) => {
  const a = toInt(appointments);
  const n = toInt(newPatients);
  return a > 0 ? (n / a) * 100 : 0;
};

/** 將 mixed 陣列做數值正規化並附上 npRatio */
function normalizeMixed(mixed, labelKey) {
  return (mixed || []).map((item) => {
    const appointments = toInt(item.appointments);
    const newPatients  = toInt(item.newPatients);
    const oldPatients  = toInt(item.oldPatients);
    const npRatio      = calcNpRatio(newPatients, appointments);
    return { ...item, [labelKey]: item[labelKey], appointments, newPatients, oldPatients, npRatio };
  });
}

/** 產表：['標題1','標題2',...] + items[]  */
function makeTable(headers, items) {
  return { headers, items };
}

/** 提示框 formatter（柱＋比率點共用） */
function tooltipAxisFormatter(params) {
  const lines = [params?.[0]?.axisValueLabel || ''];
  params.forEach((p) => {
    const isRatio = p.seriesName === 'NP比例';
    const val = p.value == null ? '-' : p.value;
    lines.push(`${p.marker}${p.seriesName}: ${isRatio ? Number(val).toFixed(1) + '%' : val}`);
  });
  return lines.join('<br/>');
}

/* ------------------------------------------------------------------ */
/* ① 圖表建構：可重用且可命名                                             */
/* ------------------------------------------------------------------ */

/** 工廠（底層）：建立混合圖的 ECharts options */
function buildMixedChartOptions(labels, oldArr, newArr, ratioArr, opts = {}) {
  const { rightAxisName = 'NP比例', leftAxisName = '預約數量', enableDataZoom = false } = opts;
  return {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, formatter: tooltipAxisFormatter },
    legend: { type: 'scroll' },
    grid: { top: 32, right: 56, left: 56, bottom: 24, containLabel: true },
    dataZoom: enableDataZoom ? [{ type: 'slider', startValue: Math.max(0, labels.length - 12), endValue: labels.length - 1 }] : [],
    xAxis: { type: 'category', data: labels, axisLabel: { interval: 0 } },
    yAxis: [
      { type: 'value', name: leftAxisName, min: 0 },
      { type: 'value', name: rightAxisName, position: 'right', axisLabel: { formatter: '{value} %' }, splitLine: { show: false } },
    ],
    series: [
      { name: '舊病人', type: 'bar', stack: 'Stack 1', data: oldArr },
      { name: '新病人', type: 'bar', stack: 'Stack 1', data: newArr },
      { name: 'NP比例', type: 'scatter', yAxisIndex: 1, data: ratioArr.map((v) => Number(v.toFixed(1))), showSymbol: true, symbolSize: 8 },
    ],
  };
}

/** 工廠（底層）：建立折線圖的 ECharts options */
function buildLineChartOptions({ labels, seriesNames, seriesMatrix, yName = '預約數量' }) {
  return {
    tooltip: { trigger: 'axis' },
    legend: { type: 'scroll', data: seriesNames },
    xAxis: { type: 'category', data: labels },
    yAxis: { type: 'value', name: yName },
    series: seriesNames.map((name, i) => ({
      name, type: 'line', data: seriesMatrix[i], smooth: true,
    })),
  };
}

/** ✅ 對外匯出：建立「混合圖」物件，支援命名 */
export function buildMixedChart({ title = '混合圖', labels, oldArr, newArr, ratioArr, opts }) {
  return {
    name: title,
    chartType: 'mixed',
    chartOptions: buildMixedChartOptions(labels, oldArr, newArr, ratioArr, opts),
  };
}

/** ✅ 對外匯出：建立「折線圖」物件，支援命名 */
export function buildLineChart({ title = '折線圖', labels, seriesNames, seriesMatrix, yName }) {
  return {
    name: title,
    chartType: 'line',
    chartOptions: buildLineChartOptions({ labels, seriesNames, seriesMatrix, yName }),
  };
}

/* ------------------------------------------------------------------ */
/* ② formatter：把資料轉換成 table + chart，並允許覆蓋圖名               */
/* ------------------------------------------------------------------ */

function makeFormattersByLabel({ labelKey, enableDataZoomWhen = (len) => len > 20 }) {
  function formatMixed(mixed, { mixedName } = {}) {
    const rows   = normalizeMixed(mixed, labelKey);
    const labels = rows.map((d) => d[labelKey]);
    const oldArr = rows.map((d) => d.oldPatients);
    const newArr = rows.map((d) => d.newPatients);
    const ratio  = rows.map((d) => d.npRatio);

    const headLabel =
      labelKey === 'month' ? '月份' :
        labelKey === 'source' ? '來源' :
          labelKey === 'result' ? '處理結果' :
            labelKey === 'treatment' ? '治療項目' : '分類';

    const table = makeTable(
      [headLabel, '預約數', 'NP數', 'NP比例'],
      rows.map((r) => ({
        [headLabel]: r[labelKey],
        預約數: r.appointments,
        NP數: r.newPatients,
        NP比例: `${r.npRatio.toFixed(1)}%`,
      }))
    );

    const chart = buildMixedChart({
      title: mixedName || '混合圖',
      labels,
      oldArr,
      newArr,
      ratioArr: ratio,
      opts: { enableDataZoom: enableDataZoomWhen(labels.length) },
    });

    return { chart, table };
  }

  function formatLine(line, { lineName } = {}) {
    const labels = (line || []).map((d) => d.date);
    const seriesNames = Object.keys(line?.[0] || {}).filter((k) => k !== 'date');
    const matrix = seriesNames.map((name) => line.map((d) => d[name]));

    const table = makeTable(
      ['月份', ...seriesNames],
      line.map((row) => {
        const out = { 月份: row.date };
        seriesNames.forEach((n) => { out[n] = row[n]; });
        return out;
      })
    );

    const chart = buildLineChart({
      title: lineName || '折線圖',
      labels,
      seriesNames,
      seriesMatrix: matrix,
      yName: '預約數量',
    });

    return { chart, table };
  }

  /** 封裝：輸入 { mixed?, line? }，得到 { availableCharts } */
  function formatAll(data, options = {}) {
    const hasMixed = Array.isArray(data?.mixed) && data.mixed.length > 0;
    const hasLine  = Array.isArray(data?.line);
    if (!hasMixed) return { availableCharts: {} };

    const mixed = formatMixed(data.mixed, options);
    const charts = { mixed: { ...mixed.chart, tableData: mixed.table } };

    if (hasLine) {
      const line = formatLine(data.line, options);
      charts.line = { ...line.chart, tableData: line.table };
    }

    return { availableCharts: charts };
  }

  return { formatAll };
}

/* ===== 對外 API（舊名保留；多一個可選 options 覆蓋圖名） ===== */
export function formatMonthlyData(data, options = {}) {
  const { mixedName = '混合圖', lineName = '折線圖', enableDataZoom = false } = options;

  const source = data?.mixed || [];
  if (source.length === 0) return { availableCharts: {} };

  // 數據前處理（與你原本一致）
  const processedData = source.map(item => {
    const appointments = parseInt(item.appointments, 10);
    const newPatients  = parseInt(item.newPatients, 10);
    const oldPatients  = parseInt(item.oldPatients, 10);
    const npRatio      = appointments > 0 ? (newPatients / appointments) * 100 : 0;
    return { ...item, appointments, newPatients, oldPatients, npRatio };
  });

  const labels   = processedData.map(d => d.month);
  const oldArr   = processedData.map(d => d.oldPatients);
  const newArr   = processedData.map(d => d.newPatients);
  const ratioArr = processedData.map(d => d.npRatio);

  // 表格（共用一份）
  const tableData = {
    headers: ['月份', '預約數', 'NP數', 'NP比例'],
    items: processedData.map(item => ({
      '月份': item.month,
      '預約數': item.appointments,
      'NP數': item.newPatients,
      'NP比例': `${item.npRatio.toFixed(1)}%`
    }))
  };

  // ── 混合圖：使用工廠 ──
  const mixedOption = buildMixedChartOptions(labels, oldArr, newArr, ratioArr, { enableDataZoom });

  // ── 折線圖：在此函式內獨立組裝（雙軸，與你原本行為一致） ──
  const lineOption = {
    // 若你專案有全局主題，這裡可移除 color，交由主題接管
    // color: ['#8884d8', '#82ca9d', '#ffc658'],
    tooltip: { trigger: 'axis' },
    legend: { data: ['總預約數', 'NP數量', 'NP比例'] },
    xAxis: { type: 'category', data: labels },
    yAxis: [
      { name: '數量', type: 'value', position: 'left', splitLine: { show: true } },
      { name: '百分比', type: 'value', position: 'right', axisLabel: { formatter: '{value} %' }, splitLine: { show: false } }
    ],
    series: [
      { name: '總預約數', type: 'line', yAxisIndex: 0, data: processedData.map(i => i.appointments), smooth: true },
      { name: 'NP數量',   type: 'line', yAxisIndex: 0, data: processedData.map(i => i.newPatients),  smooth: true },
      { name: 'NP比例',   type: 'line', yAxisIndex: 1, data: processedData.map(i => Number(i.npRatio.toFixed(1))), smooth: true }
    ]
  };

  return {
    availableCharts: {
      mixed: {
        name: mixedName,       // ← 外部可覆蓋
        chartType: 'mixed',
        chartOptions: mixedOption,
        tableData,
      },
      line: {
        name: lineName,        // ← 外部可覆蓋
        chartType: 'line',
        chartOptions: lineOption,
        tableData,
      },
    }
  };
}

export function formatSourceData(data, options) {
  return makeFormattersByLabel({ labelKey: 'source' }).formatAll(data, options);
}

export function formatProcessingResultData(data, options) {
  return makeFormattersByLabel({ labelKey: 'result' }).formatAll(data, options);
}

export function formatTreatmentData(data, options) {
  return makeFormattersByLabel({
    labelKey: 'treatment',
    enableDataZoomWhen: (len) => len > 20,
  }).formatAll(data, options);
}
